import { useState, type ReactElement } from 'react';
import styles from './index-row.module.scss';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import RunnerIndexView from '@utmb/types/view/RunnerIndexView';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';
import { IndexCard } from '../index-card/index-card';
import { IndexGeneralCard } from '../index-general-card/index-general-card';
import { IndexDetailsPopin } from '../index-details-popin/index-details-popin';
import { Link } from '@utmb/design-system/link/link';
import { useTranslation } from 'next-i18next';
import { Font } from '@utmb/design-system/text/font';
import FrozenIndexView from '@utmb/types/view/FrozenIndexView';
import { useUser } from '@utmb/contexts/AuthContext';

export interface IndexRowProps {
    performanceIndexes: RunnerIndexView[];
    frozenIndexes?: FrozenIndexView[];
    results?: RaceYearResultForRunnerView[];
    variant?: 'myUtmb';
}

const SUB_CATEGORIES: PiCategoryEnum[] = [PiCategoryEnum.PI20K, PiCategoryEnum.PI50K, PiCategoryEnum.PI100K, PiCategoryEnum.PI100M];

export function IndexRow({ performanceIndexes, frozenIndexes, results, variant }: IndexRowProps): ReactElement {
    const { t } = useTranslation();
    const { isAuthenticated } = useUser();
    const [popinCategory, setPopinCategory] = useState<PiCategoryEnum | null>(null);

    const getIndex = (category: PiCategoryEnum): number | undefined => performanceIndexes.find((p) => p.piCategory === category)?.index;
    const getFrozenIndex = (category: PiCategoryEnum): number | undefined => {
        const index = frozenIndexes?.find((p) => p.piCategory === category)?.index;
        return index !== undefined ? Math.round(index) : undefined;
    };
    const getFrozenRanking = (category: PiCategoryEnum): number | null | undefined => frozenIndexes?.find((p) => p.piCategory === category)?.rankSex;

    const openDetails = results ? (category: PiCategoryEnum) => () => setPopinCategory(category) : undefined;

    const hasFrozen = !!frozenIndexes && frozenIndexes.length > 0;

    return (
        <div className={variant === 'myUtmb' ? styles.wrapIndexRow + ' ' + styles.myUtmb : styles.wrapIndexRow}>
            <div className={styles.indexRow}>
                <div className={styles.mainIndex}>
                    <IndexGeneralCard variant={variant} value={getIndex(PiCategoryEnum.GENERAL)} onDetailsClick={openDetails?.(PiCategoryEnum.GENERAL)} />
                </div>
                {SUB_CATEGORIES.map((category) => (
                    <div key={category} className={styles.index}>
                        <IndexCard category={category} value={getIndex(category)} onDetailsClick={openDetails?.(category)} />
                    </div>
                ))}
            </div>

            {hasFrozen && (
                <div className={styles.indexRow}>
                    <div className={styles.mainIndex}>
                        <IndexGeneralCard
                            isFrozen
                            variant={variant}
                            value={getFrozenIndex(PiCategoryEnum.GENERAL)}
                            ranking={getFrozenRanking(PiCategoryEnum.GENERAL)}
                        />
                    </div>
                    {SUB_CATEGORIES.map((category) => (
                        <div key={category} className={styles.index}>
                            <IndexCard isFrozen category={category} value={getFrozenIndex(category)} ranking={getFrozenRanking(category)} />
                        </div>
                    ))}
                </div>
            )}

            <Link className={styles.link} href="/utmb-index/info#slice5" target="_blank">
                <Font.FuturaBook desktop="16" mobile="14">
                    {t('singleRunner.statistics.global.howIItWork')}
                </Font.FuturaBook>
            </Link>

            {popinCategory && results && (
                <IndexDetailsPopin
                    results={results}
                    category={popinCategory}
                    score={getIndex(popinCategory)}
                    isAuthenticated={!!isAuthenticated}
                    close={() => setPopinCategory(null)}
                />
            )}
        </div>
    );
}
